import React, { useEffect, useRef, useState } from 'react';
import { Input } from './input';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';

// Define types for the Google Maps API
declare global {
  interface Window {
    google: any; // Use any type to avoid conflicts with other declarations
  }
}

// Define missing Google Maps types
namespace google.maps {
  export interface Geocoder {
    geocode(
      request: {
        placeId: string;
      },
      callback: (
        results: Array<{
          address_components: Array<{
            long_name: string;
            short_name: string;
            types: string[];
          }>;
          formatted_address: string;
        }>,
        status: string
      ) => void
    ): void;
  }

  export namespace places {
    export interface AutocompleteService {
      getPlacePredictions(
        request: {
          input: string;
          types?: string[];
          componentRestrictions?: {
            country: string;
          };
        },
        callback: (
          predictions: Array<{
            description: string;
            place_id: string;
            structured_formatting: {
              main_text: string;
              secondary_text: string;
            };
          }>,
          status: string
        ) => void
      ): void;
    }

    export interface AutocompleteSessionToken {}
  }
}

// Extend Geocoder result type to include geometry
interface GeocoderResult {
  address_components: Array<{
    long_name: string;
    short_name: string;
    types: string[];
  }>;
  formatted_address: string;
  geometry: {
    location: {
      lat: () => number;
      lng: () => number;
    };
  };
}

export interface PlaceType {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface AddressComponents {
  street_number?: string;
  route?: string;
  locality?: string;
  sublocality?: string;
  neighborhood?: string;
  administrative_area_level_2?: string;
  administrative_area_level_1?: string;
  postal_code?: string;
  formatted_address?: string;
  lat?: number;
  lng?: number;
}

interface GooglePlacesAutocompleteProps {
  onAddressSelect: (address: AddressComponents) => void;
  placeholder?: string;
  value?: string;
  required?: boolean;
  error?: string;
  id?: string;
  name?: string;
  onChange?: (value: string) => void;
  className?: string;
  autocomplete?: string;
  dropdownPosition?: 'above' | 'below' | 'auto';
}

export function GooglePlacesAutocomplete({
  onAddressSelect,
  placeholder = 'Search for an address',
  value = '',
  required = false,
  error,
  id,
  name,
  onChange,
  className = '',
  autocomplete,
  dropdownPosition = 'below',
}: GooglePlacesAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<PlaceType[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isGoogleMapsReady, setIsGoogleMapsReady] = useState(false);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const geocoder = useRef<google.maps.Geocoder | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    let checkGoogleInterval: NodeJS.Timeout | null = null;
    let retryCount = 0;
    const maxRetries = 5;
    
    const initializeGoogleMaps = () => {
      // Clear any existing interval
      if (checkGoogleInterval) {
        clearInterval(checkGoogleInterval);
        checkGoogleInterval = null;
      }
      
      // Check if Google Maps API is already fully loaded and ready
      if (window.google?.maps?.places?.AutocompleteService && window.google?.maps?.Geocoder) {
        initServices();
        return true;
      }
      
      // If API objects aren't available, wait for them with retry logic
      checkGoogleInterval = setInterval(() => {
        retryCount++;
        
        if (window.google?.maps?.places?.AutocompleteService && window.google?.maps?.Geocoder) {
          if (checkGoogleInterval) {
            clearInterval(checkGoogleInterval);
            checkGoogleInterval = null;
          }
          initServices();
        } else if (retryCount >= maxRetries) {
          if (checkGoogleInterval) {
            clearInterval(checkGoogleInterval);
            checkGoogleInterval = null;
          }
          console.error('Google Maps API failed to load after multiple retries. Autocomplete will not work.');
        }
      }, 200);
      
      return false;
    };
    
    // Try to initialize immediately
    if (!initializeGoogleMaps()) {
      // If not ready, check if script exists in DOM
      const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]') as HTMLScriptElement;
      
      if (existingScript) {
        // Script exists, wait for it to load
        // Check if script has already loaded
        const scriptElement = existingScript as any; // Cast to access readyState property
        if (scriptElement.readyState === 'complete' || scriptElement.readyState === 'loaded' || existingScript.src) {
          // Script is already loaded or loading, wait for API to be ready
          setTimeout(initializeGoogleMaps, 100);
        } else {
          // Script is still loading
          const handleScriptLoad = () => {
            setTimeout(initializeGoogleMaps, 100);
            existingScript.removeEventListener('load', handleScriptLoad);
            existingScript.removeEventListener('error', handleScriptError);
          };
          
          const handleScriptError = () => {
            console.error('Google Maps script failed to load');
            existingScript.removeEventListener('load', handleScriptLoad);
            existingScript.removeEventListener('error', handleScriptError);
          };
          
          existingScript.addEventListener('load', handleScriptLoad);
          existingScript.addEventListener('error', handleScriptError);
        }
      } else {
        // No script found, create and load it
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
        script.async = true;
        script.defer = true;
        script.id = 'google-maps-script';
        
        script.onload = () => {
          setTimeout(initializeGoogleMaps, 100);
        };
        
        script.onerror = () => {
          console.error('Failed to load Google Maps script');
        };
        
        document.head.appendChild(script);
      }
    }
    
    // Cleanup function
    return () => {
      if (checkGoogleInterval) {
        clearInterval(checkGoogleInterval);
      }
    };
  }, []);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const initServices = () => {
    // Double-check that all required Google Maps API objects are available
    if (window.google?.maps?.places?.AutocompleteService && window.google?.maps?.Geocoder) {
      try {
        // Only initialize if not already initialized
        if (!autocompleteService.current) {
          autocompleteService.current = new window.google.maps.places.AutocompleteService();
        }
        if (!geocoder.current) {
          geocoder.current = new window.google.maps.Geocoder();
        }
        
        setIsGoogleMapsReady(true);
      } catch (error) {
        console.error("Error initializing Google Maps services:", error);
        // Reset services on error so they can be retried
        autocompleteService.current = null;
        geocoder.current = null;
        setIsGoogleMapsReady(false);
      }
    } else {
      console.warn("Google Maps API not fully loaded yet - required objects missing");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setHighlightedIndex(-1);

    if (value.length > 2) {
      // If service is not available, try to reinitialize
      if (!autocompleteService.current && window.google?.maps?.places?.AutocompleteService) {
        initServices();
      }
      
      if (autocompleteService.current) {
        try {
          autocompleteService.current.getPlacePredictions(
            {
              input: value,
              types: ['address'],
              componentRestrictions: { country: 'us' }, // Restrict to US addresses
            },
            (predictions, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                setSuggestions(predictions);
                setShowSuggestions(true);
              } else {
                // Log status for debugging
                setSuggestions([]);
                setShowSuggestions(false);
              }
            }
          );
        } catch (error) {
          console.warn("Error getting place predictions:", error);
          // Reset service and try to reinitialize for next attempt
          autocompleteService.current = null;
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } else {
        // Service not available, clear suggestions
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }

    // Call onChange prop with the new value
    onChange?.(value);
  };

  const handleSuggestionClick = (placeId: string, description: string) => {
    setInputValue(description);
    setShowSuggestions(false);

    // If geocoder service is not available, try to reinitialize
    if (!geocoder.current && window.google?.maps?.Geocoder) {
      initServices();
    }

    if (geocoder.current) {
      try {
        geocoder.current.geocode({ placeId }, (results, status) => {
          const geocoderResults = results as GeocoderResult[];
          if (status === window.google.maps.GeocoderStatus.OK && geocoderResults && geocoderResults[0]) {
            const addressComponents = geocoderResults[0].address_components;
            const formattedAddress = geocoderResults[0].formatted_address;
            const geometry = geocoderResults[0].geometry;

            // Extract address components
            const addressData: AddressComponents = {
              formatted_address: formattedAddress,
              lat: geometry.location.lat(), // Extract latitude
              lng: geometry.location.lng(), // Extract longitude
            };

            addressComponents.forEach((component) => {
              const types = component.types;

              if (types.includes('street_number')) {
                addressData.street_number = component.long_name;
              }

              if (types.includes('route')) {
                addressData.route = component.long_name;
              }

              if (types.includes('locality')) {
                addressData.locality = component.long_name;
              }

              // Capture alternative city/place names for addresses without locality
              if (types.includes('sublocality') || types.includes('sublocality_level_1')) {
                addressData.sublocality = component.long_name;
              }

              if (types.includes('neighborhood')) {
                addressData.neighborhood = component.long_name;
              }

              if (types.includes('administrative_area_level_2')) {
                addressData.administrative_area_level_2 = component.long_name;
              }

              if (types.includes('administrative_area_level_1')) {
                addressData.administrative_area_level_1 = component.short_name;
              }

              if (types.includes('postal_code')) {
                addressData.postal_code = component.long_name;
              }
            });

            onAddressSelect(addressData);
          } else {
            console.error("Failed to geocode address with place ID:", placeId);
          }
        });
      } catch (error) {
        console.error("Error geocoding address:", error);
        // Reset geocoder service for next attempt
        geocoder.current = null;
      }
    } else {
      console.warn("Geocoder service not available");
    }
  };

  const handleBlur = () => {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    // Arrow down
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    }
    
    // Arrow up
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
    }
    
    // Enter
    if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      const suggestion = suggestions[highlightedIndex];
      handleSuggestionClick(suggestion.place_id, suggestion.description);
    }
    
    // Escape
    if (e.key === 'Escape') {
      e.preventDefault();
      setShowSuggestions(false);
    }
  };

  return (
    <div className="relative w-full">
      <div className="flex h-10 w-full items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px] focus-within:border-primary disabled:cursor-not-allowed disabled:opacity-50">
        <Input
          ref={inputRef}
          type="text"
          placeholder={isGoogleMapsReady ? placeholder : "Loading address search..."}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={cn(
            "w-full border-0 bg-transparent p-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground",
            error && 'text-destructive',
            className
          )}
          required={required}
          id={id}
          name={name}
          autoComplete={autocomplete}
        />
        {inputValue && (
          <button
            type="button"
            onClick={() => {
              setInputValue('');
              setSuggestions([]);
              setShowSuggestions(false);
              if (inputRef.current) {
                inputRef.current.focus();
              }
            }}
            className="ml-2 h-4 w-4 opacity-70 hover:opacity-100 flex-shrink-0"
            aria-label="Clear input"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        )}
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className={cn(
            "absolute z-[9999] w-full overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-lg",
            dropdownPosition === 'above' ? "bottom-full mb-1" : "top-full mt-1",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          <div 
            className="p-1 overflow-y-auto max-h-60"
            onWheel={(e) => {
              // Allow wheel scrolling within this container
              e.stopPropagation();
            }}
          >
            {suggestions.length > 8 && (
              <div className="flex cursor-default items-center justify-center py-1">
                <ChevronUp className="h-4 w-4 opacity-50" />
              </div>
            )}
            
            {suggestions.map((suggestion, index) => (
              <div
                key={suggestion.place_id}
                className={cn(
                  "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-2 text-sm outline-none",
                  index === highlightedIndex ? "bg-accent text-accent-foreground" : "hover:bg-accent hover:text-accent-foreground"
                )}
                onClick={() => handleSuggestionClick(suggestion.place_id, suggestion.description)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="flex flex-col">
                  <div className="font-medium">{suggestion.structured_formatting.main_text}</div>
                  <div className="text-xs text-muted-foreground">{suggestion.structured_formatting.secondary_text}</div>
                </div>
              </div>
            ))}
            
            {suggestions.length > 8 && (
              <div className="flex cursor-default items-center justify-center py-1">
                <ChevronDown className="h-4 w-4 opacity-50" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
